import { useMemo } from 'react'
import {
  PopoverHorizontalPositionProps,
  PopoverVerticalPositionProps
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  DecoratedUserTypes,
  UserTypes,
  DecoratedUserTypes as UserDataDecoratedUserTypes
} from '../../../../services/user/user-data'
import {
  CHECKOUT_CLICKED_WITHOUT_LOGIN_KEY,
  CORESITE_LOCALSTORAGE,
  HYBRIS_ACCESS_TOKEN,
  ORDER_TYPE_ZAS,
  ORDER_TYPE_ZBC,
  ORDER_TYPE_ZKL,
  ORDER_TYPE_ZLD,
  ORDER_TYPE_ZTA
} from '../../../../utils/constants'
import {
  DataLayerAddToCartProduct,
  DataLayerEcommerceProduct,
  pushDataLayerMiniCartSeeCartClick,
  pushDataLayerMiniCartValidateOrderClick
} from '../datalayer/product'
import type { CbxProductInput } from './types'
import { checkIsPreorder } from '../../../../utils/preorder'
import { getPriceInfoMiniCart } from '../price-info'
import { getAssetImageUrl } from '../../../../utils/asset-url'
import { ProductDataVariant } from '../../../../services/product/product-data'
import {
  CbxConfigType,
  HybrisConfig,
  MuleSoftConfigType
} from '../../../../utils/page-config'
import { ShopConfig } from '../config'

import type { HandleAddToCartProps } from './handleAddToCart'
import { getReplacementSerialNumber } from '../../ProductList/utils/replacement-product'

const outOfStockValue = 'outOfStock'

export const getAddToCartButtonLabel = ({
  guestAddToCartEnabled,
  preOrderUntilDate,
  stockLevelStatus,
  addToCartLabel,
  preOrderLabel,
  notifyMe,
  notifyMeCTALabel,
  outOfStockLabel,
  loginToBuyLabel,
  isProductSet,
  userData,
  isMultiProduct
}: {
  guestAddToCartEnabled: boolean | undefined
  preOrderUntilDate: string | undefined
  stockLevelStatus: string | undefined
  addToCartLabel: string | undefined
  preOrderLabel: string | undefined
  notifyMe: boolean | undefined
  notifyMeCTALabel: string | undefined
  outOfStockLabel: string | undefined
  loginToBuyLabel: string | undefined
  isProductSet?: boolean
  userData?: {
    user?: object
    decoratedUserType?: DecoratedUserTypes | null
  } | null
  /**
   * True when more than one product is configured (bundle). Notify Me is never
   * offered for bundles, so the OOS label is used for out-of-stock instead.
   */
  isMultiProduct?: boolean
}) => {
  const isGuestAddToCart =
    guestAddToCartEnabled || userData?.decoratedUserType !== UserTypes.Las

  const isPreorder = checkIsPreorder(preOrderUntilDate)

  // Suppress "Notify Me" for multi-product (bundle) configurations.
  const notifyMeEnabled = isMultiProduct ? false : notifyMe
  const isOutOfStock = stockLevelStatus === outOfStockValue

  let buttonLabel = addToCartLabel

  if (isPreorder && isGuestAddToCart) {
    buttonLabel = preOrderLabel
  }

  if (isGuestAddToCart && !isPreorder && notifyMeEnabled && isOutOfStock) {
    buttonLabel = notifyMeCTALabel
  }

  if (!isPreorder && !notifyMeEnabled && isOutOfStock && !isProductSet) {
    buttonLabel = outOfStockLabel
  }

  if (!isGuestAddToCart && (isPreorder || notifyMeEnabled || !isOutOfStock)) {
    buttonLabel = loginToBuyLabel
  }

  return buttonLabel
}

interface ProductPriceInfo {
  totalWithoutDiscounts?: {
    formattedValue: string
  }
  totalPriceWithoutDiscounts?: {
    formattedValue: string
  }
  totalPrice?: {
    formattedValue: string
  }
}

export interface CartProductEntry extends ProductPriceInfo {
  entryNumber?: number
  product: {
    ageVerified?: boolean
    analitycsColorName?: string
    analyticsProductName?: string
    availableForPickup?: boolean
    baseProduct?: string
    brandCode?: string
    brandFamily?: string
    canonicalUrl?: string
    categories?: Array<{
      code: string
      name: string
      url: string
    }>
    code?: string
    color?: {
      colorCode?: string
      colorGroup?: {
        code: string
        hexCode: string
        name: string
      }
    }
    defaultCategory?: {
      code: string
    }
    deviceVersion?: string
    ean?: string
    edition?: string
    groupedByBaseProduct?: boolean
    isRedemptionEligible?: boolean
    isReviewable?: string
    isRewardEligible?: boolean
    mainCategory?: string
    maxOrderQuantity?: number
    minOrderQuantity?: number
    name?: string
    pack?: boolean
    platformBrand?: string
    platformBrandCode?: string
    pmiProductCategory?: string
    pmiProductCode?: string
    pmiSellableProductCode?: string
    preOrderDateUntil?: string
    preOrderable?: boolean
    productAvailableBadges?: {
      productAvailableBadge: Array<{
        label: string
        background: string
      }>
    }
    productType?: string
    productUrls?: Array<{
      language: string
      url: string
    }>
    purchasable?: boolean
    redemptionPoints?: string
    seoDescription?: string
    seoNoArchive?: boolean
    seoNoFollow?: boolean
    seoNoIndex?: boolean
    seoTitle?: string
    seoURL?: string
    statusPoints?: string
    stock?: {
      value?: string
    }
    subjectedToRegistration?: boolean
    unit?: {
      code: string
      conversion: number
      name: string
    }
    url?: string
    variantProduct?: boolean
  }
  quantity?: number
}

interface CartModificationEntry extends ProductPriceInfo {
  entryNumber: number
  entryGroupNumbers: Array<number>
  bundleName: string
  bundleQty: number
  productReference: string
}

interface CartModification {
  quantity: number
  entry: CartModificationEntry
}

interface CartProductSet extends ProductPriceInfo {
  code: string
  entryGroupNumber: number
}

interface Cart {
  entries: Array<CartProductEntry>
  productSetInfo?: {
    productSets: Array<CartProductSet>
  }
}

export const getProductSetEntry = (
  cartModifications: Array<CartModification>,
  cart: Cart
) => {
  const entryGroupNumber = cartModifications?.[0]?.entry?.entryGroupNumbers?.[0]
  const bundleName = cartModifications?.[0]?.entry?.bundleName
  const bundleQty = cartModifications?.[0]?.entry?.bundleQty
  const productSetInfo = cart?.productSetInfo?.productSets?.find(
    (productSet) => productSet?.entryGroupNumber === entryGroupNumber
  )
  return {
    quantity: bundleQty,
    product: {
      code: productSetInfo?.code,
      name: bundleName
    },
    totalPrice: productSetInfo?.totalPrice,
    totalPriceWithoutDiscounts: productSetInfo?.totalPriceWithoutDiscounts,
    totalWithoutDiscounts: productSetInfo?.totalPriceWithoutDiscounts
  }
}

export const getChildProductEntries = (
  cartModifications: Array<CartModification>,
  cart: Cart
) =>
  cartModifications?.map((cartModification) =>
    cart?.entries?.find(
      (entry) => entry?.entryNumber === cartModification?.entry?.entryNumber
    )
  )

const getMiniCartProductProps = (
  productEntry: CartProductEntry,
  variants: Array<ProductDataVariant>,
  showOldPrices?: boolean
): {
  productName?: string
  productVariantName?: string
  productQuantity?: number
  priceInfoProps?: {
    currentPriceTextProps?: {
      content?: string
    }
    originalPriceTextProps?: {
      content?: string
    }
  }
  productImageProps?: {
    height: string
    width: string
    src: string
  }
  productChipsProps?: {
    chipPropsList: Array<{
      label: string
      background?: string
    }>
  }
} => {
  const addedProductCode = productEntry?.product?.code
  const propsOfAddedProduct = addedProductCode
    ? variants?.find(
        (variant) => variant?.globalArticleCode === addedProductCode
      )
    : undefined

  const addedProductBadges =
    productEntry?.product?.productAvailableBadges?.productAvailableBadge &&
    productEntry.product.productAvailableBadges.productAvailableBadge.length > 0
      ? productEntry.product.productAvailableBadges.productAvailableBadge.map(
          (badgeProps) => ({
            label: badgeProps.label,
            background: badgeProps.background
          })
        )
      : undefined

  const miniCartAssetUrl = getAssetImageUrl(propsOfAddedProduct?.assets?.[0])
  const productImageProps = miniCartAssetUrl
    ? {
        height: '100',
        width: '100',
        src: `${miniCartAssetUrl}?width=100`
      }
    : undefined

  const priceInfoProps = getPriceInfoMiniCart(
    productEntry?.totalWithoutDiscounts?.formattedValue,
    productEntry?.totalPrice?.formattedValue,
    showOldPrices
  )

  return {
    productName: productEntry?.product?.name,
    productVariantName: (
      productEntry?.product as { nuance?: { description?: string } }
    )?.nuance?.description,
    productQuantity: productEntry?.quantity,
    priceInfoProps,
    ...(productImageProps ? { productImageProps } : {}),
    ...(addedProductBadges
      ? { productChipsProps: { chipPropsList: addedProductBadges } }
      : {})
  }
}

export const useMiniCartConfig = ({
  productEntry,
  childProductEntries,
  config,
  variants,
  cartLocalStorageJSON,
  activeMiniCart,
  setActiveMiniCart,
  productQuantityLabel
}: {
  productEntry: CartProductEntry
  childProductEntries?: Array<CartProductEntry>
  config: {
    successMessage: string
    checkoutCTALabel: string
    checkoutCTAUrl: string
    cartCTALabel: string
    cartCTAUrl: string
    closeButtonAriaLabel: string
    showOldPrices: boolean
  }
  variants: Array<ProductDataVariant>
  cartLocalStorageJSON: {
    cart: {
      totalUnitCount: string
    }
  }
  activeMiniCart: boolean
  setActiveMiniCart: (activeMiniCart: boolean) => void
  productQuantityLabel: string
}) => {
  const configPopoverMiniCart = useMemo(
    () => ({
      open: activeMiniCart,
      verticalPositionAnchor: PopoverVerticalPositionProps.Top,
      horizontalPositionAnchor: PopoverHorizontalPositionProps.Right,
      verticalTransform: PopoverVerticalPositionProps.Top,
      horizontalTransform: PopoverHorizontalPositionProps.Center
    }),
    [activeMiniCart]
  )

  return {
    popoverProps: configPopoverMiniCart,
    title: config?.successMessage,
    onClose: () => setActiveMiniCart(false),
    productQuantityLabel,
    actions: [
      {
        label: config?.checkoutCTALabel,
        linkUrl: config?.checkoutCTAUrl,
        onClick: () => {
          sessionStorage.setItem(CHECKOUT_CLICKED_WITHOUT_LOGIN_KEY, 'true')
          pushDataLayerMiniCartValidateOrderClick()
        }
      },
      {
        label: `${config?.cartCTALabel} (${cartLocalStorageJSON?.cart?.totalUnitCount})`,
        linkUrl: config?.cartCTAUrl,
        onClick: pushDataLayerMiniCartSeeCartClick
      }
    ],
    idleCloseTimeout: 5000,
    blurCloseTimeout: 3000,
    'aria-label': config?.closeButtonAriaLabel,
    ...getMiniCartProductProps(productEntry, variants, config?.showOldPrices),
    ...(childProductEntries
      ? {
          ...{
            childProductInfoProps: childProductEntries.map(
              (childProductEntry: CartProductEntry) => ({
                ...getMiniCartProductProps(
                  childProductEntry,
                  variants,
                  config?.showOldPrices
                ),
                productQuantityLabel
              })
            )
          }
        }
      : {})
  }
}

interface CartLocalStorageJSON {
  cart?: {
    totalUnitCount?: string
  }
}

export type SetCookieType = (
  name: string,
  value: string,
  options?: {
    path?: string
    maxAge?: number
    secure?: boolean
    sameSite?: 'strict' | 'lax' | 'none'
  }
) => void

export type SetErrorMessageContentType = (content: {
  message?: string | null
  title?: string | null
  actions?: Array<{
    label?: string
    linkUrl?: string
    onClick?: () => void | Promise<void>
    buttonVariant?: unknown
  }> | null
  dismissible?: boolean
}) => void

/**
 * Returns the cart redirect URL when the order type requires direct cart
 * navigation (ZLD lending, ZAS subscription, or ZKL product replacement),
 * otherwise returns undefined.
 */
export const getRedirectToCartUrl = (
  orderType: string,
  cartCTAUrl?: string
): string | undefined =>
  (orderType === ORDER_TYPE_ZLD ||
    orderType === ORDER_TYPE_ZAS ||
    orderType === ORDER_TYPE_ZKL) &&
  cartCTAUrl
    ? cartCTAUrl
    : undefined

export const redirectToLoginPage = (loginPageURL?: string): boolean => {
  if (loginPageURL) {
    window.location.assign(loginPageURL)
    return true
  }
  return false
}

export interface CreateAddToCartMethodConfigProps {
  orderType: string
  lendingConfigurationCode?: string
  setIsButtonDisabled: (disabled: boolean) => void
  setIsButtonLoading?: (loading: boolean) => void
  dataLayerEcommerceProduct: DataLayerEcommerceProduct
  /**
   * Data layer product data for every configured product (bundle support).
   * When provided, the add-to-cart event reports all products.
   */
  dataLayerEcommerceProducts?: DataLayerAddToCartProduct[]
  productData?: ProductDataVariant
  productID: string
  productEntries?: string[]
  /**
   * All configured products for a CBX Orders bundle. Submitted as a single
   * `products[]` request. Undefined/single-item for single-GCode configs.
   */
  cbxProducts?: CbxProductInput[]
  caConfig: {
    hybrisConfig?: HybrisConfig
    shopConfig?: ShopConfig
    market?: string
    lang?: string
    muleSoftConfig?: MuleSoftConfigType
    cbxConfig?: CbxConfigType
  }
  setActiveMiniCart: (active: boolean) => void
  cookies: {
    gig_uid?: string
    gig_jwt?: string
    hy_at?: string
  }
  setCookie: SetCookieType
  cartLocalStorageJSON?: CartLocalStorageJSON | null
  setCartLocalStorage: (value: string) => void
  setErrorMessageContent: SetErrorMessageContentType
  setDialogErrorMessageIsOpen: (open: boolean) => void
  userData?: {
    decoratedUserType?: UserDataDecoratedUserTypes | null
  } | null
  productQuantity?: number
}

export const createAddToCartMethodConfig = (
  props: CreateAddToCartMethodConfigProps
) => {
  const {
    orderType,
    lendingConfigurationCode,
    setIsButtonDisabled,
    setIsButtonLoading,
    dataLayerEcommerceProduct,
    dataLayerEcommerceProducts,
    productData,
    productID,
    productEntries,
    cbxProducts,
    caConfig,
    setActiveMiniCart,
    cookies,
    setCookie,
    cartLocalStorageJSON,
    setCartLocalStorage,
    setErrorMessageContent,
    setDialogErrorMessageIsOpen,
    userData,
    productQuantity
  } = props

  const { hybrisConfig, shopConfig, market, lang, muleSoftConfig, cbxConfig } =
    caConfig || {}

  const CA_HYBRIS_URL = hybrisConfig?.hybrisUrl
  const CA_CLIENT_ID = hybrisConfig?.hybrisClientId
  const CA_CLIENT_SECRET = hybrisConfig?.hybrisClientSecret
  const CA_HYBRIS_BASE_SITE_ID = hybrisConfig?.baseSiteId
  const CA_HYBRIS_LANGUAGE = hybrisConfig?.language

  return {
    orderType,
    lendingConfigurationCode,
    redirectToCartOnSuccess: getRedirectToCartUrl(
      orderType,
      shopConfig?.cartCTAUrl
    ),
    setIsButtonDisabled,
    setIsButtonLoading,
    setErrorMessageContent,
    isLoginUser: userData?.decoratedUserType !== UserTypes.Las,
    errorContent: {
      exceededMaximumQuantityTitle: shopConfig?.exceededMaximumQuantityTitle,
      exceededMaximumQuantityMessage:
        shopConfig?.exceededMaximumQuantityMessage,
      differentOrderTypeTitle: shopConfig?.differentOrderTypeTitle,
      differentOrderTypeMessage: shopConfig?.differentOrderTypeMessage,
      differentLoyaltyRequiredTitle: shopConfig?.differentLoyaltyRequiredTitle,
      differentLoyaltyRequiredMessage:
        shopConfig?.differentLoyaltyRequiredMessage,
      differentOrderTypeConfirmCTALabel:
        shopConfig?.differentOrderTypeConfirmCTALabel,
      loginRequiredTitle: shopConfig?.loginRequiredTitle,
      loginRequiredMessage: shopConfig?.loginRequiredMessage,
      loginRequiredCTALabel: shopConfig?.loginRequiredCTALabel,
      loginPageURL: shopConfig?.loginPageURL,
      addToCartDialogCloseAriaLabel: shopConfig?.addToCartDialogCloseAriaLabel,
      addToCartDialogCancelCTALabel: shopConfig?.addToCartDialogCancelCTALabel
    },
    productID,
    productEntries,
    cbxProducts,
    productQuantity:
      orderType === ORDER_TYPE_ZAS || orderType === ORDER_TYPE_ZKL
        ? 1
        : productQuantity || 1,
    currentProductVariant: productData,
    hybrisConfig: {
      CA_HYBRIS_URL,
      CA_CLIENT_ID,
      CA_CLIENT_SECRET,
      CA_HYBRIS_BASE_SITE_ID,
      CA_HYBRIS_LANGUAGE,
      HYBRIS_ACCESS_TOKEN
    },
    defaultErrorMessage: shopConfig?.addToCartErrorMessage,
    mulesoftConfig: muleSoftConfig || undefined,
    market: market || undefined,
    dataLayerEcommerceProduct,
    dataLayerEcommerceProducts,
    setCookie,
    cookies,
    CORESITE_LOCALSTORAGE,
    language: lang || '',
    setDialogErrorMessageIsOpen,
    cartLocalStorageJSON: cartLocalStorageJSON || null,
    setCartLocalStorage,
    setActiveMiniCart,
    // Always provide cbxOrdersConfig when a CBX URL is configured, for ALL
    // order types. This lets the CBX_ORDERS_CART QA toggle govern standard PDP
    // purchases (via shouldUseCbxCart) — matching the PLP variant-data-builder.
    ...(cbxConfig?.cbxUrl
      ? {
          cbxOrdersConfig: {
            baseURL: cbxConfig.cbxUrl,
            brand: cbxConfig.brand,
            channel: cbxConfig.xChannel,
            countryCode: cbxConfig.xCountryCode,
            targetGroup: 'b2c' as const,
            loggerEnabled: cbxConfig.loggerEnabled
          }
        }
      : {}),
    // useCbxOrders forces CBX regardless of the CBX_ORDERS_CART toggle:
    //  - ZAS/ZKL are already-migrated order types — always CBX.
    //  - ZTA/ZBC route to CBX ONLY for multi-product bundles (DCXWAM-28372),
    //    which require the CBX products[] batch call. A single-GCode ZTA/ZBC is
    //    NOT forced here, so it follows the toggle (Hybris when OFF) — restoring
    //    the pre-bundle single-product behavior (ticket AC/SC-008).
    ...((orderType === ORDER_TYPE_ZAS ||
      orderType === ORDER_TYPE_ZKL ||
      ((orderType === ORDER_TYPE_ZTA || orderType === ORDER_TYPE_ZBC) &&
        (cbxProducts?.length ?? 0) > 1)) &&
    cbxConfig?.cbxUrl
      ? { useCbxOrders: true }
      : {}),
    ...(orderType === ORDER_TYPE_ZKL
      ? {
          replacementType: 'DEVICE' as const,
          replacedSerialNumber: getReplacementSerialNumber() || undefined
        }
      : {})
  } as HandleAddToCartProps
}
